import { useCallback, useEffect, useRef, useState } from 'react';
import type { DragEvent } from 'react';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ TAKING A FILE SOMEBODY DRAGGED IN. C48-1.                            │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * `file-helpers.ts` is the other direction — handing somebody a file — and this is deliberately not
 * in it: that module has no React in it at all, and a hook would give it a renderer.
 *
 * ⚠️⚠️ **THE MECHANISM IS HERE AND THE WORDS ARE NOT.** What a drop target looks like and what it
 * says are the screen's (rule 7 puts the strings in the catalogue), so this returns a boolean and
 * four handlers and draws nothing. `import-dialog.tsx` has the same four inline and one flag; it is
 * left alone, because its target is a 200px box that cannot have the problem this exists to solve —
 * see the counter below.
 *
 * ⚠️ **Four things go wrong with a hand-written file drop, and three of them are silent:**
 *
 *  1. **No `preventDefault` on `dragover` means no `drop` at all** — the browser navigates to the
 *     file instead, which on a single-page app throws away everything on screen. The commonest
 *     version of this bug looks like *"drag-and-drop does nothing"* while it is actually the
 *     document winning.
 *  2. **`dragleave` fires when the pointer crosses into a CHILD element**, so a naive
 *     `onDragLeave={() => setOver(false)}` flickers the whole time somebody moves over a thread of
 *     nested nodes. Hence the depth counter rather than a boolean.
 *  3. **A drag that ends outside the window may deliver no `dragleave`**, which leaves the counter
 *     above zero and the overlay stuck over a screen nobody can use. Hence the window reset.
 *  4. **Dragging selected text lights it up** unless the drag is checked for actually carrying
 *     files. `dataTransfer.types` is the only thing readable during a drag — the file list itself is
 *     deliberately empty until the drop, so *"is this a file"* has exactly one answer available.
 *
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ AND A FIFTH, WHICH IS WHY `enabled` DOES NOT MEAN *"IGNORE THE DROP"*.│
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The first version of this hook returned early when disabled, on the reasoning that swallowing a
 * drop nothing will act on is how a file appears to vanish. That gets the trade backwards, because
 * *not* swallowing it is not *"nothing happens"* — it is **the document winning**, and the document's
 * behaviour for a dropped file is to navigate the tab to it. On a single-page app that unmounts
 * everything: the unsent draft, the thread, the state.
 *
 * So the drag is **always** swallowed once it is known to carry files, and `enabled` decides only
 * whether the files are handed on. A disabled target is silent, which is acceptable precisely
 * because every state that disables it already says so on screen — a notice under the composer, or
 * the paperclip showing an upload in flight. A destroyed page says nothing at all.
 */

/**
 * ⚠️ **`Array.from` rather than `.includes` on `types` directly.** It is `readonly string[]` in the
 * TypeScript DOM lib and a live `DOMStringList` in some engines, and the second has no `includes`.
 */
function carriesFiles(transfer: DataTransfer): boolean {
  return Array.from(transfer.types).includes('Files');
}

export interface FileDrop {
  /** Whether a file is currently over the target — the screen decides what that looks like. */
  readonly over: boolean;
  /** Spread onto the element that should accept the drop. */
  readonly handlers: {
    readonly onDragEnter: (event: DragEvent<HTMLElement>) => void;
    readonly onDragOver: (event: DragEvent<HTMLElement>) => void;
    readonly onDragLeave: (event: DragEvent<HTMLElement>) => void;
    readonly onDrop: (event: DragEvent<HTMLElement>) => void;
  };
}

export function useFileDrop({
  enabled,
  onFiles,
}: {
  /**
   * Whether the files are handed on — **not** whether the drop is intercepted.
   *
   * ⚠️ **A disabled target still swallows the drop**, for the reason set out in the header: letting
   * the document have it navigates the tab to the file and takes the whole page with it.
   */
  readonly enabled: boolean;
  /** Every file the drop carried, unfiltered — what is acceptable is the caller's question. */
  readonly onFiles: (files: readonly File[]) => void;
}): FileDrop {
  const [over, setOver] = useState(false);
  const depth = useRef(0);

  const reset = useCallback(() => {
    depth.current = 0;
    setOver(false);
  }, []);

  /**
   * ⚠️ **On `window`, and both events, because either can be the last one this element hears.** A
   * drag released over another application delivers `dragend` to the source and nothing here; a drop
   * on a sibling element delivers `drop` there. Neither is our drop, and both mean the drag is over.
   */
  useEffect(() => {
    window.addEventListener('dragend', reset);
    window.addEventListener('drop', reset);
    return () => {
      window.removeEventListener('dragend', reset);
      window.removeEventListener('drop', reset);
    };
  }, [reset]);

  const onDragEnter = useCallback(
    (event: DragEvent<HTMLElement>) => {
      if (!carriesFiles(event.dataTransfer)) return;
      event.preventDefault();
      if (!enabled) return;
      depth.current += 1;
      setOver(true);
    },
    [enabled],
  );

  const onDragOver = useCallback(
    (event: DragEvent<HTMLElement>) => {
      if (!carriesFiles(event.dataTransfer)) return;
      event.preventDefault();
      /* ⚠️⚠️ **`none` WHEN DISABLED, AND IT IS THE ONLY HONEST CURSOR FOR A SWALLOWED DROP.** The
       * drag is intercepted either way, so the platform would otherwise draw a copy cursor over a
       * target that is going to do nothing — a promise made by the pointer and broken by the
       * handler. ⚠️ And `copy` when enabled, because the default over an intercepted target is the
       * *"you cannot drop that here"* glyph, and people believe the cursor. */
      event.dataTransfer.dropEffect = enabled ? 'copy' : 'none';
      if (!enabled) return;
      /* ⚠️ Also set here, not only on enter: a drag that began INSIDE this element (over the
       * textarea, say) has already fired its `dragenter` on a child before this one sees anything. */
      setOver(true);
    },
    [enabled],
  );

  const onDragLeave = useCallback((event: DragEvent<HTMLElement>) => {
    event.preventDefault();
    depth.current = Math.max(0, depth.current - 1);
    if (depth.current === 0) setOver(false);
  }, []);

  const onDrop = useCallback(
    (event: DragEvent<HTMLElement>) => {
      /* ⚠️ Unconditional, and the header argues why: an un-prevented drop navigates the tab. */
      event.preventDefault();
      reset();
      if (!enabled) return;
      const dropped = Array.from(event.dataTransfer.files);
      if (dropped.length > 0) onFiles(dropped);
    },
    [enabled, onFiles, reset],
  );

  return { over, handlers: { onDragEnter, onDragOver, onDragLeave, onDrop } };
}
